export function formatPhoneNumber(s: string): string {
  const s2 = ('' + s).replace(/\D/g, '')
  const m = /^(\d{3})(\d{3})(\d{4})$/.exec(s2)
  return !m ? s : '(' + m[1] + ') ' + m[2] + '-' + m[3]
}

/**
 * Converts a Java enum like SEND_OTHER to display (Send Other).
 */
export function formatEnum(enumStr: string) {
  const str = enumStr.toLowerCase()
  const strparts = str.split('_')
  return strparts.map(p => p.charAt(0).toUpperCase() + p.substring(1)).join(' ')
}

export function formatOptPhoneNumber(s?: string | null): string | null {
  if (s == null || s === '') {
    return null
  }
  return formatPhoneNumber(s)
}

export function formatContactName(contact: any): string | null {
  const { firstName, lastName } = contact
  if (firstName == null && lastName == null) {
    return null
  }
  return [firstName, lastName].join(' ')
}

export function formatError(err: Error) {
  let { message } = err
  if (message.startsWith('GraphQL error: ')) {
    message = message.substring('GraphQL error: '.length)
  }
  return message
}

export function formatPasswordDots(pass: string): string {
  return new Array(pass.length + 1).join('●')
}

export function formatShortUrl(url: string, maxChars?: number): string {
  let ret
  const parts = url.split('/')
  if (parts.length < 3 || parts[1] !== '') {
    ret = url
  } else {
    const domain = parts[2]
    const domainParts = domain.split('.')
    const shortDom = domainParts.slice(domainParts.length - 2).join('.')
    ret = [shortDom].concat(parts.slice(3).filter(s => s.trim() !== '')).join('/')
  }

  const maxLen = maxChars || 10
  return ret.length <= maxLen ? ret : ret.substr(0, maxLen - 1) + '…'
}

// Formats a time in milliseconds as, for example, "2:55" or "0:20"
export function formatDuration(millis: number): string {
  const minsUntil = Math.floor(millis / 60 / 1000)
  const secsUntil = Math.floor(millis / 1000) % 60
  const secsUntil00 = ('' + (secsUntil + 100)).substring(1)
  return minsUntil + ':' + secsUntil00
}

/**
 * Returns a horizontal rule string (consisting of em dash characters)  of length n
 */
export function hr(n: number): string {
  return '—'.repeat(n)
}

/**
 * Returns the word or the plural version of the word based on n
 */
export function plural(n: number, s: string): string {
  if (n <= 1) {
    return s
  }
  return s + 's'
}

/**
 * plural(n, "person") returns eg "1 person" or "2 people"
 */
export function nPlural(n: number, str: string): string {
  if (n === 1) {
    return '1 ' + str
  } else if (str === 'person') {
    return n + ' people'
  } else {
    return n + ' ' + str + 's'
  }
}

export function nullToEmpty(val?: string): string {
  return val == null ? '' : val
}

export function formatNullDash<T>(val: T | null, fn: (t: T) => string): string {
  if (val == null) {
    return '—'
  }
  return fn(val)
}

/**
 * formatUSD(1234) return "$1,234"
 */
export function formatUSD(usd: number): string {
  return '$' + formatInt(usd)
}

/**
 * formatInt(1234) return "1,234"
 */
export function formatInt(val: number): string {
  const str = val.toFixed(0)
  if (val < 1000) {
    return str
  }
  const parts = []
  for (let i = str.length - 3; i > -3; i -= 3) {
    parts.unshift(str.substring(Math.max(i, 0), i + 3))
  }
  return parts.join(',')
}

/**
 * aOrAn("apple") returns "an apple"
 * aOrAn("car") returns "a car"
 */
export function aOrAn(nextWord: string) {
  if (nextWord) {
    const vowels = ['a', 'e', 'i', 'o', 'u']
    return (vowels.includes(nextWord.toLowerCase().substring(0, 1)) ? 'an ' : 'a ') + nextWord
  } else {
    return null
  }
}

/**
 * Returns the list of things as a string, e.g.:
 * ['apple'] --> 'apple'
 * ['apple', 'pear'] --> 'apple and pear'
 * ['apple', 'pear', 'banana'] --> 'apple, pear, and banana'
 */
export function listThingsInEnglish(things: string[]) {
  const n = things.length
  if (n === 0) {
    return ''
  } else if (n === 1) {
    return things[0]
  } else if (n === 2) {
    return things[0] + ' and ' + things[1]
  } else {
    let s = ''
    for (let i = 0; i < n - 1; i++) {
      s += things[i]
      s += ', '
    }
    return s + 'and ' + things[n - 1]
  }
}

/**
 * Takes a string formatted in camelCase and returns
 */
export function camelToTitleCase(camelCaseStr: string) {
  const str = camelCaseStr.replace(/([A-Z0-9])/g, ' $1')
  return titleCase(str)
}

export function titleCase(str: string) {
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase()
}

/**
 * <code>
 * capitalize("jim") returns "Jim"
 * capitalize("BOB") returns "Bob"
 * </code>
 */
export function capitalize(str: string) {
  return str.substring(0, 1).toUpperCase() + str.substring(1).toLowerCase()
}
